// PSC Billing — Dashboard.
// A multi-page operational dashboard with consistent chrome on every page,
// a left rail for navigation, and a Data Quality page that tells the truth
// about what to trust.
import { useCallback } from 'react';
import { dashboardApi } from '../../api/dashboard.js';
import { useApi } from '../../hooks';
import { useUrlState } from '../../url';
import { SubNav } from './Chrome.jsx';
import { dqRules, dqScore } from './helpers.js';
import { PageBlockers }    from './pageBlockers.jsx';
import { PageDataQuality } from './pageDataQuality.jsx';
import { PageLocations }   from './pageLocations.jsx';
import { PageOverview }    from './pageOverview.jsx';
import { PagePipeline }    from './pagePipeline.jsx';
import { PagePushToday }   from './pagePushToday.jsx';
import { PageTrends }      from './pageTrends.jsx';
import { PageWatchlist }   from './pageWatchlist.jsx';
import './dashboard.css';

const PAGE_COMPONENTS = {
  overview:     PageOverview,
  pipeline:     PagePipeline,
  push_today:   PagePushToday,
  blockers:     PageBlockers,
  trends:       PageTrends,
  locations:    PageLocations,
  watchlist:    PageWatchlist,
  data_quality: PageDataQuality,
};

// Dashboard tiles hand Records a pre-set filter. Translating the intent here
// keeps the query-param vocabulary in one place instead of spread across eight
// sub-pages — and it's the same vocabulary a hand-written link uses.
function recordsQuery(intent = {}) {
  return {
    filter: intent.filter && intent.filter !== 'all' ? intent.filter : null,
    site_code: intent.siteCode || null,
    customer_ot: intent.customerOt || null,
    exclusion_reason: intent.exclusionReason || null,
  };
}

export default function Dashboard() {
  const { data: stats, error } = useApi(dashboardApi.get, []);
  const { segments, depth, navigate, back } = useUrlState();

  // The sub-page is the second path segment: /dashboard/blockers. Every tile
  // click is a real navigation, so Back walks the drill-down the user actually
  // took and any dashboard page can be linked to directly.
  const requested = segments[1];
  const subPage = PAGE_COMPONENTS[requested] ? requested : 'overview';

  const goSubPage = useCallback((id) => {
    if (!PAGE_COMPONENTS[id]) return;
    navigate(['dashboard', id === 'overview' ? null : id]);
  }, [navigate]);

  // Cross-tab navigation helpers — pass these to every sub-page.
  const goRecords    = useCallback((intent) => navigate(['records'], recordsQuery(intent)), [navigate]);
  const goExtraction = useCallback(() => navigate(['extraction']), [navigate]);
  const goAudit      = useCallback(() => navigate(['audit']),      [navigate]);

  if (error) return <div className="db-error">{String(error)}</div>;
  if (!stats) return <div className="db-loading">Loading dashboard…</div>;

  const PageComp = PAGE_COMPONENTS[subPage] || PageOverview;
  const tone = dqScore(dqRules(stats)).tone;

  const nav = {
    goSubPage,
    goRecords,
    goExtraction,
    goAudit,
    // The chrome's Back button IS the browser's. ``depth`` counts in-app
    // navigations, so it goes grey exactly when Back would leave the app
    // rather than move within it.
    goBack: back,
    canBack: depth > 0,
  };

  return (
    <div className="db-shell">
      <SubNav active={subPage} onSelect={goSubPage} dqTone={tone} />
      <div className="db-content">
        <PageComp stats={stats} nav={nav} />
      </div>
    </div>
  );
}

